import { Button } from "@/components/ui/button";
import { cn } from "@/utils/common.utils";
import { DEAL_TABS } from "@/constant/deals.constant";

interface DealTabItem {
  k: string;
  l: string;
}

interface DealTabsProps {
  tab: string;
  onChange: (tab: string) => void;
  className?: string;
  /** Defaults to every tab. DealDetail narrows this when a feature is off;
   *  the client and collaborator portals pass their own short list. */
  items?: readonly DealTabItem[];
  /** Unread counts keyed by tab, rendered as a pill beside the label. */
  badges?: Partial<Record<string, number>>;
}

export default function DealTabs({ tab, onChange, className = "", items = DEAL_TABS, badges }: DealTabsProps) {
  return (
    <div className={cn("-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0", className)} role="tablist" aria-label="Deal sections">
      <div className="flex min-w-max items-center gap-1 sm:gap-2">
        {items.map((item) => {
          const active = tab === item.k;
          const badge = badges?.[item.k] || 0;
          return (
            <Button variant="seamless"
              key={item.k}
              type="button"
              onClick={() => onChange(item.k)}
              data-testid={`tab-${item.k}`}
              role="tab"
              aria-selected={active}
              className={cn(
                "relative h-11 shrink-0 overflow-hidden rounded-t-xl px-4 text-sm font-medium transition-colors duration-200 sm:px-5",
                // The `seamless` variant ships hover:text-current/hover:bg-transparent.
                // The active branch must restate its own hover colours or those win
                // and the label washes out against the green fill.
                active
                  ? "text-white hover:text-white"
                  : "text-dash-muted hover:bg-dash-searchBg hover:text-dash-ink"
              )}
            >
              {active && (
                <span
                  className="absolute inset-0 bg-primary animate-in fade-in"
                  aria-hidden="true"
                />
              )}
              <span className="relative z-10 inline-flex items-center gap-2">
                {item.l}
                {badge > 0 && (
                  <span
                    className={cn(
                      "flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-semibold",
                      active ? "bg-white text-primary" : "bg-primary text-white",
                    )}
                    aria-label={`${badge} unread`}
                    data-testid={`tab-${item.k}-badge`}
                  >
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </span>
            </Button>
          );
        })}
      </div>
    </div>
  );
}
